import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The replay's clock: a simulated time of day that runs forward while
 * playing, at a chosen multiple of real time, and can be scrubbed.
 */
export interface ReplayClock {
  /** Simulated minutes after midnight, fractional while playing. */
  time: number;
  /** The whole minute the views read their samples at. */
  minute: number;
  playing: boolean;
  /** Simulated minutes per real minute: ×60 is one simulated minute per real second. */
  speed: number;
  atEnd: boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  setSpeed: (speed: number) => void;
  seek: (minute: number) => void;
}

interface Options {
  /** The span the clock may run over, in minutes. */
  from: number;
  to: number;
  /** Where the clock starts, and returns to when the span changes under it. */
  initial?: number | null;
  speed?: number;
  /** Called when the whole minute changes, however the clock moved. */
  onMinute?: (minute: number) => void;
}

/** Frames are painted no more often than this: plenty for buses, kind to the rest of the page. */
const PAINT_EVERY_MS = 33;

/** A frame longer than this is a hidden tab coming back; the clock does not leap to catch up. */
export const MAX_FRAME_S = 0.1;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * Where the clock stands after one frame: `elapsedS` real seconds at
 * `speed` simulated minutes per real minute, never past `to`, and never a
 * leap — a long gap between frames counts as a short one.
 */
export function stepClock(time: number, elapsedS: number, speed: number, to: number): { time: number; atEnd: boolean } {
  const dt = Math.min(MAX_FRAME_S, Math.max(0, elapsedS));
  const next = time + (dt * speed) / 60;
  if (next >= to) return { time: to, atEnd: true };
  return { time: next, atEnd: false };
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function useReplayClock({ from, to, initial, speed: initialSpeed = 120, onMinute }: Options): ReplayClock {
  const start = clamp(initial ?? from, from, to);
  const [time, setTime] = useState(start);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeedState] = useState(initialSpeed);

  // The frame loop owns the time between paints; state only follows it.
  const timeRef = useRef(start);
  const minuteRef = useRef(Math.floor(start));
  const onMinuteRef = useRef(onMinute);
  onMinuteRef.current = onMinute;

  const commit = useCallback((t: number) => {
    timeRef.current = t;
    setTime(t);
    const m = Math.floor(t);
    if (m !== minuteRef.current) {
      minuteRef.current = m;
      onMinuteRef.current?.(m);
    }
  }, []);

  // New runs, new span: a clock outside it goes back to the start.
  useEffect(() => {
    if (timeRef.current < from || timeRef.current > to) commit(start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => {
    if (!playing) return;
    const reduced = prefersReducedMotion();
    let raf = 0;
    let last = performance.now();
    let lastPaint = 0;
    const frame = (now: number) => {
      const step = stepClock(timeRef.current, (now - last) / 1000, speed, to);
      last = now;
      if (step.atEnd) {
        commit(to);
        setPlaying(false);
        return;
      }
      timeRef.current = step.time;
      if (now - lastPaint >= PAINT_EVERY_MS) {
        lastPaint = now;
        // With reduced motion the picture steps a minute at a time instead of gliding.
        commit(reduced ? Math.floor(step.time) : step.time);
        timeRef.current = step.time;
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, to, commit]);

  const play = useCallback(() => {
    // Play from the end means play again from the start.
    if (timeRef.current >= to) commit(from);
    setPlaying(true);
  }, [from, to, commit]);
  const pause = useCallback(() => setPlaying(false), []);
  const toggle = useCallback(() => {
    setPlaying((p) => {
      if (!p && timeRef.current >= to) commit(from);
      return !p;
    });
  }, [from, to, commit]);
  const seek = useCallback((minute: number) => {
    commit(clamp(minute, from, to));
  }, [from, to, commit]);
  const setSpeed = useCallback((s: number) => setSpeedState(Math.max(1, s)), []);

  return {
    time,
    minute: Math.floor(time),
    playing,
    speed,
    atEnd: time >= to,
    play, pause, toggle, setSpeed, seek,
  };
}
